package ui.campaign.detailv2.editor.page

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.Crossfade
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import api.VisualRotaryMode
import compose.assets.Svg
import compose.assets.svg.Delete_outline
import compose.assets.svg.Shuffle_variant
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import tk.mallumo.log.logINFO
import tools.*
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.NextButton
import ui.campaign.detailv2.editor.widget.*
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperEVisualDynamic
import ui.campaign.detailv2.editor.wrapper.ImplCampaignWrapperEVisual
import ui.snackBarStateGlobal
import ui.widget.DialogState
import ui.widget.EditorCardWrapper
import ui.widget.blockTouch

@Composable
fun CampaignEditorScope.CampaignPage04VisualDynamic(nnvl: CampaignWrapperE) {
    val scrollState = nav.rememberLazyListState("visual-dynamic")
    val scope = rememberCoroutineScope()
    Box(Modifier.fillMaxSize()) {
        Crossfade(
            targetState = nnvl.visualDynamic.adapterGroups.state.isEmpty(),
            modifier = Modifier.fillMaxSize()
        ) { noConditions ->
            if (noConditions) ExplainUi(Res.string.layer_top_detail_editor__dynamic_visual_info)
            else ContentUi(nnvl, scrollState)
        }

        AddActionButton {
            nnvl.visualDynamic.createGroup()
            scope.launch {
                scrollState.animateScrollToItem(0)
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CampaignEditorScope.ContentUi(nnvl: CampaignWrapperE, scrollState: LazyListState) {


    val scope = rememberCoroutineScope()

    var showOverlayLoader by remember {
        mutableStateOf(false)
    }

    GlobalObjectShare.Consumer(nnvl.visualDynamic.adapterGroups.state.map { it.visuals.state.map { it.pickerKey } }.flatten())

    val validationDialogState = DialogState.remember(false, "visualDynamicValidator")

    fun openResourcePicker(item: ImplCampaignWrapperEVisual.AdapterVisual.Item) {
        GlobalObjectShare.launchImagePicker(
            key = item.pickerKey,
            nav = nav,
            args = item.pickerArgs
        ) {
            it.firstOrNull()?.apply {
                val entry = item.createVisualEntry(this)
                if (item.isVisualDurationOk(this)) item.set(entry)
                else nnvl.visualDynamic.combinationToCheck = item to entry
            }
        }
    }

    DialogVisualDurationValidation(
        state = validationDialogState,
        nnvl = nnvl,
        wrapper = nnvl.visualDynamic,
        canChangeSettings = false,
        onPickDifferent = { item ->
            validationDialogState.hide()
            openResourcePicker(item)
        },
        onOpenGeneralSettings = { _, _ -> },
        onDurationChange = { _, _, _ -> },
        onIncreaseDuration = { item, resource, duration ->
            showOverlayLoader = true
            scope.launch {
                runCatching {
                    item.set(vm.resizeDuration(resource, duration))
                }.onFailure {
                    launch {
                        snackBarStateGlobal.showSnackbar(it.message!!)
                    }
                }
                showOverlayLoader = false
            }
            validationDialogState.hide()
        },
        onReduceDuration = { item, resource, duration ->
            showOverlayLoader = true
            scope.launch {
                runCatching {
                    item.set(vm.resizeDuration(resource, duration))
                }.onFailure {
                    launch {
                        snackBarStateGlobal.showSnackbar(it.message!!)
                    }
                }
                showOverlayLoader = false
            }
            validationDialogState.hide()
        })

    LaunchedEffect(nnvl.visualDynamic.combinationToCheck) {
        if (nnvl.visualDynamic.combinationToCheck != null) {
            logINFO("show checker")
            validationDialogState.show()
        }
    }

    LazyColumn(
        state = scrollState,
        contentPadding =
            if (display.isMobileOrSmall) PaddingValues(vertical = 16.dp)
            else PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.fillMaxSize()
    ) {
        if (nnvl.visualDynamic.adapterGroups.state.isNotEmpty()) {
            item {
                ShuffleMode(nnvl.visualDynamic)
            }
        }
        items(nnvl.visualDynamic.adapterGroups.state) { group ->
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceContainer
                )
            ) {
                with(group.conditions) {
                    CampaignConditionHeader(
                        entryId = group.key,
                        actionsFooter = {
                            OutlinedButton(
                                onClick = {
                                    nnvl.visualDynamic.adapterGroups.removeAll {
                                        it.key == group.key
                                    }
                                },
                                modifier = Modifier.align(Alignment.Center)
                            ) {
                                Icon(Svg.Delete_outline, null)
                                Text(Res.string.layer_top_detail_editor__visual_dynamic_remove.rememberString())
                            }
                        })
                    ItemContent(nnvl, group) {
                        openResourcePicker(it)
                    }
                }
            }
        }
        item {
            NextButton()
        }
    }

    AnimatedVisibility(
        visible = showOverlayLoader,
        modifier = Modifier.fillMaxSize(),
        enter = fadeIn(),
        exit = fadeOut()
    ) {
        Box(
            Modifier.fillMaxSize()
                .background(Color.Black.copy(alpha = 0.7F))
                .blockTouch()
        ) {
            CircularProgressIndicator(Modifier.align(Alignment.Center))
        }
    }
}


@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CampaignEditorScope.ShuffleMode(wrapper: CampaignWrapperEVisualDynamic) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        EditorCardWrapper(
            icon = Svg.Shuffle_variant,
            title = Res.string.layer_top_detail_editor__dynamic_visual_rotary_title,
            tooltip = Res.string.layer_top_detail_editor__dynamic_visual_rotary_tooltip
        ) {
            ShuffleModeItem(
                title = Res.string.layer_top_detail_editor__dynamic_visual_rotary_dynamic,
                mask = VisualRotaryMode.DYNAMIC_VISUALS,
                visualRotaryMode = wrapper.visualRotaryMode
            )
            ShuffleModeItem(
                title = Res.string.layer_top_detail_editor__dynamic_visual_rotary_primary,
                mask = VisualRotaryMode.PRIMARY_VISUALS,
                visualRotaryMode = wrapper.visualRotaryMode
            )
        }
    }
}

@Composable
private fun ShuffleModeItem(
    title: StringResource,
    mask: VisualRotaryMode,
    visualRotaryMode: EFieldRegularInt
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(16.dp),
        modifier = Modifier.padding(start = 16.dp)
    ) {
        Switch(
            checked = mask match visualRotaryMode.state,
            onCheckedChange = { enable ->
                println("initial: ${visualRotaryMode.state} (${title.key})")
                if (enable) {
                    visualRotaryMode.set(visualRotaryMode.state + mask.mask)
                    println("enable: ${visualRotaryMode.state} (${title.key})")
                } else {
                    visualRotaryMode.set(visualRotaryMode.state - mask.mask)
                    println("disable: ${visualRotaryMode.state} (${title.key})")
                }
            })
        Text(
            title.rememberString(),
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CampaignEditorScope.ItemContent(nnvl: CampaignWrapperE, group: ImplCampaignWrapperEVisual.AdapterGroup.Item, openResourcePicker: (ImplCampaignWrapperEVisual.AdapterVisual.Item) -> Unit) {
    FlowRow(
        Modifier.fillMaxWidth()
            .wrapContentHeight(Alignment.Top)
            .padding(16.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        group.visuals.state
            .filter { it.data.mask in nnvl.visualDynamic.deviceRatioMasks }
            .sortedWith(compareBy({ !it.data.land }, { Int.MAX_VALUE - it.data.mask }))
            .forEach {
                ListingEntryVisualItem(
                    item = it,
                    openIssueResolver = {
                        nnvl.visual.combinationToCheck = it to it.resource!!
                    },
                    openResourcePicker = {
                        nav.storeScrollState("visual-dynamic")
                        openResourcePicker(it)
                    })
            }
    }
}